import ApiClientOpen from '../api/axios/apiClientOpen';
import type { TipoPedidoListResponse, TipoPedidoResponse, TipoPedidoRequest } from '../../types';
import qs from 'qs';
import type { StrapiFilters, StrapiSort, StrapiPopulate, StrapiPagination } from '../../types/_strapi.model';

const TIPO_PEDIDO_ENDPOINT = '/tipo-pedidos';

interface GetAllTipoPedidosParams {
  filters?: StrapiFilters;
  sort?: StrapiSort | string[];
  fields?: string[];
  populate?: StrapiPopulate | string[];
  pagination?: StrapiPagination;
}

interface GetAllParams {
  page: number;
  limit: number;
  filter?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Service class for TipoPedido entity operations
 */
class TipoPedidoService {
  /**
   * Get all tipo-pedidos with optional filtering, sorting, population, and pagination
   * @param params Object containing filters, sort, fields, populate, and pagination options
   * @returns Promise with list of tipo-pedidos and metadata
   */
  async getAllTipoPedidos(params?: GetAllTipoPedidosParams): Promise<TipoPedidoListResponse> {
    try {
      // Build query parameters using qs
      const queryParams = qs.stringify(params || {}, {
        encodeValuesOnly: true, // prettify URL
      });

      const queryString = queryParams ? `?${queryParams}` : '';
      const response = await ApiClientOpen.get<TipoPedidoListResponse>(`${TIPO_PEDIDO_ENDPOINT}${queryString}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tipo-pedidos:', error);
      throw error;
    }
  }

  /** 
   * Get all tipo-pedidos with pagination, filtering, and sorting
   * @param params Object containing pagination, filtering, and sorting options
   * @returns Promise with list of tipo-pedidos and metadata
   */
  async getAll(params: GetAllParams): Promise<TipoPedidoListResponse> {
    try {
      const { page, limit, filter, sortBy, sortOrder } = params;
      
      // Build Strapi query parameters
      const strapiParams: any = {
        pagination: {
          page,
          pageSize: limit
        }
      };
      
      // Add filter if provided
      if (filter) {
        strapiParams.filters = {
          $or: [
            { nombre: { $contains: filter } }
          ]
        }
      }
      
      // Add sorting if provided
      if (sortBy && sortOrder) {
        strapiParams.sort = `${sortBy}:${sortOrder}`;
      }
      
      // Build query parameters using qs
      const queryParams = qs.stringify(strapiParams, {
        encodeValuesOnly: true, // prettify URL
      });

      const queryString = queryParams ? `?${queryParams}` : '';
      const response = await ApiClientOpen.get<TipoPedidoListResponse>(`${TIPO_PEDIDO_ENDPOINT}${queryString}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching tipo-pedidos:', error);
      throw error;
    }
  }

  /**
   * Get a tipo-pedido by ID with optional population and field selection
   * @param id TipoPedido ID
   * @param populate Optional fields to populate
   * @param fields Optional fields to select
   * @returns Promise with tipo-pedido data
   */
  async getTipoPedidoById(
    id: number | string, 
    populate?: StrapiPopulate | string[], 
    fields?: string[]
  ): Promise<TipoPedidoResponse> {
    try {
      const queryOptions: any = {};
      
      if (populate) {
        queryOptions.populate = populate;
      }
      
      if (fields) {
        queryOptions.fields = fields;
      }
      
      const queryParams = qs.stringify(queryOptions, {
        encodeValuesOnly: true,
      });
      
      const queryString = queryParams ? `?${queryParams}` : '';
      const response = await ApiClientOpen.get<TipoPedidoResponse>(`${TIPO_PEDIDO_ENDPOINT}/${id}${queryString}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching tipo-pedido with id ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new tipo-pedido
   * @param tipoPedidoData TipoPedido data to create
   * @returns Promise with created tipo-pedido data
   */
  async createTipoPedido(tipoPedidoData: TipoPedidoRequest): Promise<TipoPedidoResponse> {
    try {
      const response = await ApiClientOpen.post<TipoPedidoResponse>(TIPO_PEDIDO_ENDPOINT, tipoPedidoData);
      return response.data;
    } catch (error) {
      console.error('Error creating tipo-pedido:', error);
      throw error;
    }
  }

  /**
   * Create a new tipo-pedido (alias method)
   * @param tipoPedidoData TipoPedido data to create
   * @returns Promise with created tipo-pedido data
   */
  async create(tipoPedidoData: TipoPedidoRequest): Promise<TipoPedidoResponse> {
    return this.createTipoPedido(tipoPedidoData);
  }

  /**
   * Update an existing tipo-pedido
   * @param id TipoPedido ID to update
   * @param tipoPedidoData Updated tipo-pedido data
   * @returns Promise with updated tipo-pedido data
   */
  async updateTipoPedido(id: number | string, tipoPedidoData: TipoPedidoRequest): Promise<TipoPedidoResponse> {
    try {
      const response = await ApiClientOpen.put<TipoPedidoResponse>(`${TIPO_PEDIDO_ENDPOINT}/${id}`, tipoPedidoData);
      return response.data;
    } catch (error) {
      console.error(`Error updating tipo-pedido with id ${id}:`, error);
      throw error;
    }
  }

  /**
   * Update an existing tipo-pedido (alias method)
   * @param id TipoPedido ID to update
   * @param tipoPedidoData Updated tipo-pedido data
   * @returns Promise with updated tipo-pedido data
   */
  async update(id: number | string, tipoPedidoData: TipoPedidoRequest): Promise<TipoPedidoResponse> {
    return this.updateTipoPedido(id, tipoPedidoData);
  }

  /**
   * Delete a tipo-pedido by ID (alias method)
   * @param id TipoPedido ID to delete
   * @returns Promise indicating success or failure
   */
  async delete(id: number | string): Promise<void> {
    return this.deleteTipoPedido(id);
  }

  /**
   * Delete a tipo-pedido by ID
   * @param id TipoPedido ID to delete
   * @returns Promise indicating success or failure
   */
  async deleteTipoPedido(id: number | string): Promise<void> {
    try {
      await ApiClientOpen.delete(`${TIPO_PEDIDO_ENDPOINT}/${id}`);
    } catch (error) {
      console.error(`Error deleting tipo-pedido with id ${id}:`, error);
      throw error;
    }
  }
}

export default new TipoPedidoService();